import { ApolloError } from '@apollo/client';
import { DateTime, DurationLikeObject } from 'luxon';

import { DISCOUNT_TYPES } from '@jebel/constants';
import {
  composeFieldValidators,
  formatPhoneNumber,
  shouldBeEmail,
  shouldBePhone,
  shouldBeURL,
  shouldBeZipCode,
} from '@jebel/utils';

import { sendToSentry } from '../sentry';

export const withFormikHandle =
  (onSubmit: (data: any, helpers?: any) => any) =>
  async (formData: any, { setErrors, ...rest }: any) => {
    try {
      await onSubmit(formData, rest);
    } catch (e: any) {
      sendToSentry(e);

      if (e instanceof ApolloError) {
        const gqlErrors = (e.graphQLErrors?.[0] as unknown as Record<string, unknown>)?.details;

        setErrors(gqlErrors);
      }
    }
  };

export const required = (value: unknown) => {
  if (value === null || value === undefined) {
    return 'Must be filled';
  }

  if (Array.isArray(value) && value.length === 0) {
    return 'Must select at least one option';
  }

  if (typeof value === 'string' && value.length === 0) {
    return 'Must have at least 1 character';
  }

  return undefined;
};

export const maxLength = (value: any) =>
  (typeof value === 'string' &&
    value.length >= 1000 &&
    'Description has to be less than 1000 characters') ||
  undefined;

export const maxTextSymbols = (value: any, max: number) => {
  if (value && typeof value === 'string') {
    return value.length <= max ? undefined : `Max ${max} symbols. Please remove extra text.`;
  }

  return undefined;
};

export const isValidGPA = (given: unknown) => {
  if (!given) {
    return undefined;
  }

  const value = Number(given);

  if (isNaN(value)) {
    return 'Must be a integer number';
  }

  if (value < 0) {
    return 'Should be higher than 0';
  }

  return undefined;
};

/**
 * Compose multiple validation functions into a single one.
 * @deprecated Use {@linkcode composeFieldValidators} from `@jebel/utils` instead.
 */
export const composeValidators = composeFieldValidators;

export const isValidPhoneCode = (phoneCode?: string) => {
  if (phoneCode) {
    const isNumber = isFinite(Number(phoneCode));
    const isGreaterThanZero = Number(phoneCode) >= 1;
    const isLessThanThousand = Number(phoneCode) < 999;
    const isSpecialSymbol = /\+/g.test(phoneCode);

    const isValid = isNumber && isGreaterThanZero && isLessThanThousand && !isSpecialSymbol;
    return isValid ? undefined : 'Invalid phone code';
  }

  return undefined;
};

/** @deprecated Use `formatPhoneNumber` from `@jebel/utils` instead. */
export const formatToPhone = formatPhoneNumber;

/** @deprecated Use `shouldBePhone` from `@jebel/utils` instead. */
export const mustBePhone = shouldBePhone;

/** @deprecated Use `shouldBeEmail` from `@jebel/utils` instead. */
export const isValidEmail = shouldBeEmail;

export const isValidPercent = (value: number) =>
  value > 0 && value <= 100 ? undefined : 'Invalid percent';

export const isCorrectDateInterval =
  (startDateRaw: string, minDateDiff: DurationLikeObject) => (value: string) => {
    const startDate = DateTime.fromISO(startDateRaw)
      .plus(minDateDiff)
      .set({ second: 0, millisecond: 0 });
    const endDate = DateTime.fromISO(value).set({ second: 0, millisecond: 0 });
    const diff = startDate.diff(endDate).as('minutes');

    if (diff > 0) {
      return 'Expiration date should be after start date';
    }
  };

/** @deprecated Use `isURL` from `@jebel/utils` instead. */
export const isValidLink = shouldBeURL;

export function isValidAmountDiscount(value: unknown, type: keyof typeof DISCOUNT_TYPES) {
  if (!value || !type) {
    return 'Must have an amount';
  }

  if (type === DISCOUNT_TYPES.free) {
    return undefined;
  }

  const number = Number(value);

  if (number < 0 || isNaN(number)) {
    return 'Must be a positive number';
  }

  if (type === DISCOUNT_TYPES.discount && number >= 100) {
    return 'Percentage are between 0% and 100%';
  }

  return undefined;
}

/** @deprecated Use `shouldBeZipCode` from `@jebel/utils` instead. */
export const isValidZipCode = shouldBeZipCode;

/** @deprecated Use `shouldBeZipCode` from `@jebel/utils` instead. */
export const isOptionalValidZipCode = shouldBeZipCode;
